import { Callable, Maybe, Optional } from '@vimeo/core/types/generic';
import { Logger } from '@vimeo/logging';

import { firebaseConfig } from '../../configs/FirebaseConfig';
import {
  EFirebasePersistenceType,
  IFirebaseApiConfiguration,
  IFirebaseDependencies,
  TFirebaseApp,
  TFirebaseApplicationModule,
  TFirebaseAuth,
  TFirebaseAuthModule,
  TFirebaseDatabase,
  TFirebasePersistence,
  TFirebaseReference,
  TFirebaseSnapshot,
  TFirebaseUser,
} from '../../types/firebase';
import { offRefEvent, onRefEvent } from './cruds';

/**
 * @param firebase - firebase application module from SDK
 * @param config - firebase API configuration
 * @param name - name of the application to initialize
 * @returns initialize firebase application by name/config or find existing one if it was initialized before
 */
export function initializeFirebaseApplication(
  firebase: TFirebaseApplicationModule,
  config: IFirebaseApiConfiguration,
  name: string,
): TFirebaseApp {
  const application: Maybe<TFirebaseApp> = firebase
    .getApps()
    .find((application) => application.name === name);

  return application || firebase.initializeApp(config, name);
}

/**
 * Create firebase application based on supplied module and config.
 * Attach auth and connection handlers, manages un-subscribers in return tuple.
 *
 * @param firebase - firebase SDK bundle
 * @param config - configuration of firebase application to create
 * @param handlers - map of callbacks handling different application / auth events
 * @returns [application, unsubscribe] tuple to handle created firebase application
 */
export async function createFirebaseApplication(
  firebase: IFirebaseDependencies,
  config: {
    name: string;
    token: string;
    config: IFirebaseApiConfiguration;
    persistence?: EFirebasePersistenceType;
  },
  handlers: {
    onPersistenceChangeFinished?: (persistence: EFirebasePersistenceType) => void;
    onPersistenceChangeFailed?: (error: Error, persistence: EFirebasePersistenceType) => void;
    onBeforeAuthStateChanged?: (user: Optional<TFirebaseUser>) => void;
    onIdTokenChanged?: (user: Optional<TFirebaseUser>) => void;
    onAuthStateChanged: (user: Optional<TFirebaseUser>) => void;
    onConnectionStateChanged?: (
      snapshot: TFirebaseSnapshot,
      app: TFirebaseApp,
      auth: TFirebaseAuth,
    ) => void;
    onClockSkewChanged?: (snapshot: TFirebaseSnapshot) => void;
  },
): Promise<[TFirebaseApp, Array<Callable>]> {
  const { config: apiConfig, token, name, persistence } = config;
  const {
    onPersistenceChangeFinished,
    onPersistenceChangeFailed,
    onBeforeAuthStateChanged,
    onIdTokenChanged,
    onAuthStateChanged,
    onConnectionStateChanged,
    onClockSkewChanged,
  } = handlers;
  const unSubscribers: Array<Callable> = [];

  const app: TFirebaseApp = initializeFirebaseApplication(firebase.firebase, apiConfig, name);
  const auth: TFirebaseAuth = firebase.auth.getAuth(app);

  // Change persistence.
  // We are using custom JWT and in-memory storage (NONE) is preferred for us.
  // Otherwise, our login / logout attempts will affect other tabs and may create issues with multi-tab interoperation.
  // Fallback is 'LOCAL', which not ideal but still OK to work and continue with default auth flow on 'catch'.
  if (persistence) {
    const persistenceImplementation: TFirebasePersistence = getPersistenceImplementationByType(
      firebase.auth,
      persistence,
    );

    await firebase.auth
      .setPersistence(auth, persistenceImplementation)
      .then(() => (onPersistenceChangeFinished ? onPersistenceChangeFinished(persistence) : null))
      .catch((error) =>
        onPersistenceChangeFailed
          ? onPersistenceChangeFailed(error, persistence)
          : Logger.getGlobal().error('Auth persistence change failed:', name, persistence, error),
      );
  }

  await firebase.auth.signInWithCustomToken(auth, token);

  // Subscribe to firebase auth status that should be in sync depending on persistence settings.
  const unSubscribeFromAuthStatus = auth.onAuthStateChanged(onAuthStateChanged);
  unSubscribers.push(unSubscribeFromAuthStatus);

  if (onBeforeAuthStateChanged) {
    // Subscribe to before firebase auth status that should be in sync depending on persistence settings.
    const unSubscribeFromBeforeAuthStatus = auth.beforeAuthStateChanged(onBeforeAuthStateChanged);
    unSubscribers.push(unSubscribeFromBeforeAuthStatus);
  }

  if (onIdTokenChanged) {
    // Subscribe to before firebase auth status that should be in sync depending on persistence settings.
    const unSubscribeFromIdTokenChanged = auth.onIdTokenChanged(onIdTokenChanged);
    unSubscribers.push(unSubscribeFromIdTokenChanged);
  }

  if (onConnectionStateChanged) {
    // Get firebase database connection.
    const database: TFirebaseDatabase = firebase.database.getDatabase(app);

    // Subscribe to firebase connectionStatus and observe current connection.
    const connectedRef: TFirebaseReference = firebase.database.ref(
      database,
      firebaseConfig.REFS.INTERNAL.CONNECTION_STATE,
    );

    // Create wrapper to provide app and auth when calling cb. Simplifies race conditions.
    const onConnectionStatusChangedCallback = (snapshot: TFirebaseSnapshot) =>
      onConnectionStateChanged(snapshot, app, auth);

    onRefEvent(
      firebase,
      connectedRef,
      'value',
      onConnectionStatusChangedCallback,
      'app-connection-state',
    );
    unSubscribers.push(() =>
      offRefEvent(firebase, connectedRef, 'value', onConnectionStatusChangedCallback),
    );

    // Subscribe to firebase clock skew changes.
    if (onClockSkewChanged) {
      const clockSkewRef: TFirebaseReference = firebase.database.ref(
        database,
        firebaseConfig.REFS.INTERNAL.SERVER_TIME_OFFSET,
      );
      onRefEvent(firebase, clockSkewRef, 'value', onClockSkewChanged, 'app-clock-skew');
      unSubscribers.push(() => offRefEvent(firebase, clockSkewRef, 'value', onClockSkewChanged));
    }
  }

  return [app, unSubscribers];
}

/**
 * @param uuid - current tab / isolated session unique identifier
 * @param base - base application identifier for session credentials
 * @returns application name with added uniqueness to isolate current environment
 */
export function generateFirebaseApplicationName(uuid: string, base: string): string {
  return `VIMEO-${uuid}:${base}`;
}

/**
 * @param auth - auth module of firebase
 * @param type - type of auth persistence
 * @returns instance of persistence implementing class
 */
export function getPersistenceImplementationByType(
  auth: TFirebaseAuthModule,
  type: EFirebasePersistenceType,
): TFirebasePersistence {
  switch (type) {
    case EFirebasePersistenceType.LOCAL_STORAGE:
      return auth.browserLocalPersistence;
    case EFirebasePersistenceType.INDEXED_DB:
      return auth.indexedDBLocalPersistence;
    case EFirebasePersistenceType.SESSION:
      return auth.browserSessionPersistence;
    case EFirebasePersistenceType.MEMORY:
      return auth.inMemoryPersistence;
  }
}
